import { cloneElement, Fragment, ReactElement, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { NS_TO_BUNDLES, TextArgs, useLocalizedText } from '../localizedText';

/** The namespace a key without a `ns:` prefix belongs to, matching i18next's default. */
const DEFAULT_NAMESPACE = 'translation';

export type LocalizedTransProps = {
  /** A `ns:key`, or a plain key in the `translation` namespace, as t() takes. */
  i18nKey: string;
  /** One entry per paired tag in the message: `<link>…</link>` needs a `link`. */
  components?: Record<string, ReactElement>;
  /** ICU arguments for the message's `{placeholders}`. A tag name in `components` wins over an
   * argument of the same name, since the tag is what the message's markup refers to. */
  values?: TextArgs;
  /**
   * What to render when there is no ARB message to render: the flag is off, the pack failed, or the
   * id is absent. Defaults to react-i18next's <Trans>, which is what these sites rendered before
   * this component existed. Pass JSX instead for a sentence the i18next source never held as one
   * string — the bulk-update sentences are assembled from separate fragment keys around a link, so
   * they have no single key for <Trans> to read.
   *
   * A function, not a node, because building the fallback reads keys ARB deliberately does not carry:
   * doing that on every render would report a missing string each time, even on the renders where the
   * ARB message is what gets shown.
   */
  fallback?: () => ReactNode;
};

/**
 * The JSX analogue of t(), for a sentence with inline markup. When the Localization flag is on it
 * renders the RedVelvetTranslations message, mapping each paired tag to the component named after
 * it; otherwise it renders `fallback`.
 *
 * Author the ARB source as a *single* message with named paired tags (`Visit our <wikiLink>wiki
 * </wikiLink>`) and keep structural markup in JSX — a sentence split into fragment keys cannot be
 * reordered by a translator, and several languages need to reorder it.
 */
export function LocalizedTrans({ i18nKey, components, values, fallback }: LocalizedTransProps) {
  const separator = i18nKey.indexOf(':');
  const prefix = separator === -1 ? DEFAULT_NAMESPACE : i18nKey.slice(0, separator);
  // Declare the prefix only when it is a namespace we hold bundles for. An unmapped or typo'd prefix
  // is a caller mistake that must degrade to react-i18next, the way t() does, rather than throw
  // during render on the strict check useLocalizedText makes of the namespaces it is handed.
  const namespace = NS_TO_BUNDLES[prefix] ? prefix : DEFAULT_NAMESPACE;
  const { rich } = useLocalizedText([namespace]);

  const nodes = rich(i18nKey, { ...values, ...tagRenderers(components) });
  if (nodes) {
    return <>{keyed(nodes)}</>;
  }
  return <>{fallback?.() ?? <Trans i18nKey={i18nKey} components={components} values={values} />}</>;
}

/**
 * A render function per tag, as the ARB formatter expects: it hands each tag's formatted children to
 * the function named after the tag, which puts them inside that tag's component.
 */
function tagRenderers(components?: Record<string, ReactElement>): TextArgs {
  return Object.fromEntries(
    Object.entries(components ?? {}).map(([tag, element]) => [
      tag,
      (children: ReactNode[]) => cloneElement(element, {}, ...children),
    ])
  );
}

// A formatted message arrives as its parts, and React wants a key on each. They are positional and
// the message is fixed, so the index is a stable identity.
function keyed(nodes: ReactNode): ReactNode {
  if (!Array.isArray(nodes)) {
    return nodes;
  }
  return nodes.map((part, index) => <Fragment key={index}>{part}</Fragment>);
}
